/* Amin Ariza — animaciones de la tienda (apariciones, progreso, volver arriba, ripple). */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = window.Shopify && Shopify.designMode;

  // Elementos que aparecen al hacer scroll. Se omiten los que ya usan
  // la animación nativa del tema (.scroll-trigger) para no duplicarla.
  const REVEAL_SELECTORS = [
    '.rich-text__blocks > *',
    '.multicolumn-list__item',
    '.collection-list__item',
    '.product-grid > .grid__item',
    '.image-with-text__media',
    '.image-with-text__content',
    '.banner__box:not(.slideshow__text)',
    '.video-section__media',
    '.footer-block',
    '.newsletter__wrapper',
    '.article-card-wrapper',
  ].join(',');

  const ACCENT_SELECTORS = '.rich-text__heading, .collection__title .title, .collection-list-title, .multicolumn__title .title';

  function initReveal(root = document) {
    if (reduceMotion || designMode || !('IntersectionObserver' in window)) {
      root.querySelectorAll(ACCENT_SELECTORS).forEach((el) => el.classList.add('aa-in'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('aa-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.08 }
    );

    root.querySelectorAll(REVEAL_SELECTORS).forEach((el) => {
      if (el.closest('.scroll-trigger, .slider--everywhere, [data-aa-skip]') || el.classList.contains('aa-reveal')) return;
      // Retraso escalonado entre hermanos para un efecto en cascada.
      const index = Array.prototype.indexOf.call(el.parentElement.children, el);
      el.style.setProperty('--aa-delay', Math.min(index, 6));
      el.classList.add('aa-reveal');
      observer.observe(el);
    });

    root.querySelectorAll(ACCENT_SELECTORS).forEach((el) => observer.observe(el));
  }

  function initProgressBar() {
    const bar = document.createElement('div');
    bar.className = 'aa-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);

    const topButton = document.createElement('button');
    topButton.type = 'button';
    topButton.className = 'aa-top';
    topButton.setAttribute('aria-label', 'Volver arriba');
    topButton.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
    topButton.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));
    document.body.appendChild(topButton);

    let ticking = false;
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? window.scrollY / max : 0;
      bar.style.setProperty('--aa-progress', progress.toFixed(4));
      topButton.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.8);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  function initRipple() {
    if (reduceMotion) return;
    document.addEventListener('pointerdown', (event) => {
      const button = event.target.closest('.button:not(.button--tertiary):not(.slider-button)');
      if (!button || button.disabled) return;
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const ripple = document.createElement('span');
      ripple.className = 'aa-ripple';
      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${event.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${event.clientY - rect.top - size / 2}px`;
      button.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove());
    });
  }

  // Rebote del contador del carrito cuando cambia la cantidad.
  function initCartBump() {
    const container = document.getElementById('cart-icon-bubble');
    if (!container) return;
    let lastCount = container.querySelector('.cart-count-bubble')?.textContent.trim();

    new MutationObserver(() => {
      const bubble = container.querySelector('.cart-count-bubble');
      const count = bubble?.textContent.trim();
      if (!bubble || count === lastCount) return;
      lastCount = count;
      bubble.classList.remove('aa-bump');
      void bubble.offsetWidth; // reinicia la animación
      bubble.classList.add('aa-bump');
    }).observe(container, { childList: true, subtree: true, characterData: true });
  }

  // Al agregar al carrito, la foto del producto "se levanta" y vuela en arco
  // hasta el ícono del carrito, que responde con un rebote y un "+N".
  const FLY_SOURCES = [
    '.product__media-item.is-active img',
    '.product__media-list img',
    '.product__media img',
    '.card__media img',
    'img',
  ];

  function findProductImage(form) {
    const scope =
      form.closest('.quick-add-modal__content, .card-wrapper, product-info, .product, .shopify-section') || document;
    for (const selector of FLY_SOURCES) {
      const img = Array.from(scope.querySelectorAll(selector)).find((el) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 20 && rect.height > 20 && el.currentSrc;
      });
      if (img) return img;
    }
    return null;
  }

  function flyToCart(form) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    const img = findProductImage(form);
    if (!cartIcon || !img) return;

    const from = img.getBoundingClientRect();
    const to = cartIcon.getBoundingClientRect();

    // Tamaño del "paquete" que vuela: proporcional a la foto, sin pasar de 220px.
    const size = Math.min(220, Math.max(90, Math.min(from.width, from.height) * 0.55));
    const startX = from.left + from.width / 2 - size / 2;
    const startY = from.top + from.height / 2 - size / 2;
    const endX = to.left + to.width / 2 - size / 2;
    const endY = to.top + to.height / 2 - size / 2;

    // Contenedor externo: movimiento horizontal. Interno: vertical con arco.
    const outer = document.createElement('div');
    outer.className = 'aa-fly';
    outer.style.left = `${startX}px`;
    outer.style.top = `${startY}px`;
    outer.style.width = outer.style.height = `${size}px`;

    const inner = document.createElement('div');
    inner.className = 'aa-fly__inner';
    const clone = document.createElement('img');
    clone.src = img.currentSrc;
    clone.alt = '';
    inner.appendChild(clone);
    outer.appendChild(inner);
    document.body.appendChild(outer);

    const duration = 900;
    const dx = endX - startX;
    const dy = endY - startY;

    outer.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(0)', offset: 0.18 }, { transform: `translateX(${dx}px)` }],
      { duration, easing: 'cubic-bezier(0.45, 0, 0.55, 1)', fill: 'forwards' }
    );

    const flight = inner.animate(
      [
        { transform: 'translateY(0) scale(0.6) rotate(0deg)', opacity: 0, offset: 0 },
        { transform: 'translateY(-18px) scale(1.05) rotate(-4deg)', opacity: 1, offset: 0.18 },
        { transform: `translateY(${dy - 60}px) scale(0.45) rotate(8deg)`, opacity: 1, offset: 0.7 },
        { transform: `translateY(${dy}px) scale(0.12) rotate(14deg)`, opacity: 0.4, offset: 1 },
      ],
      { duration, easing: 'cubic-bezier(0.55, 0, 0.35, 1)', fill: 'forwards' }
    );

    flight.onfinish = () => {
      outer.remove();
      cartIcon.classList.remove('aa-cart-hit');
      void cartIcon.offsetWidth; // reinicia la animación
      cartIcon.classList.add('aa-cart-hit');
      setTimeout(() => cartIcon.classList.remove('aa-cart-hit'), 800);

      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
      const plus = document.createElement('span');
      plus.className = 'aa-plus';
      plus.textContent = `+${quantity}`;
      plus.style.left = `${to.left + to.width / 2}px`;
      plus.style.top = `${to.top}px`;
      document.body.appendChild(plus);
      plus.addEventListener('animationend', () => plus.remove());
    };
  }

  function initFlyToCart() {
    if (reduceMotion) return;
    // Fase de captura: corre antes que product-form.js marque el botón como ocupado.
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form.closest || !form.closest('product-form')) return;
        const button = form.querySelector('[type="submit"][name="add"]');
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        flyToCart(form);
      },
      true
    );
  }

  // Menú de escritorio: se despliega al pasar el cursor y el panel se ubica
  // bajo su propio botón, corrido lo necesario para no salirse de la pantalla.
  const EDGE_MARGIN = 16;

  function keepPanelInViewport(details, panel) {
    // Se calcula con medidas sin transformar para no depender de la animación.
    const trigger = details.getBoundingClientRect();

    // Vertical: el panel arranca justo debajo del header (8px de aire).
    const header = details.closest('.header-wrapper');
    if (header) {
      const drop = header.getBoundingClientRect().bottom - trigger.bottom + 8;
      details.style.setProperty('--aa-drop', `${Math.max(Math.round(drop), 8)}px`);
    }

    // Horizontal: borde izquierdo alineado con el del botón.
    const left = trigger.left;
    const right = left + panel.offsetWidth;
    let shift = 0;
    if (right > window.innerWidth - EDGE_MARGIN) shift = window.innerWidth - EDGE_MARGIN - right;
    if (left + shift < EDGE_MARGIN) shift = EDGE_MARGIN - left;
    panel.style.setProperty('--aa-shift', `${Math.round(shift)}px`);
  }

  function initMenuHover() {
    const canHover = window.matchMedia('(hover: hover) and (min-width: 990px)');
    const menus = Array.from(document.querySelectorAll('.header__inline-menu header-menu'));

    const setOpen = (details, isOpen) => {
      const summary = details.querySelector('summary');
      if (isOpen) details.setAttribute('open', '');
      else details.removeAttribute('open');
      summary?.setAttribute('aria-expanded', String(isOpen));
    };

    menus.forEach((menu) => {
      const details = menu.querySelector('details');
      const summary = details?.querySelector('summary');
      const panel = details?.querySelector('.mega-menu__content');
      if (!details || !summary || !panel) return;
      let closeTimer;

      details.addEventListener('toggle', () => {
        if (details.open) keepPanelInViewport(details, panel);
      });

      menu.addEventListener('mouseenter', () => {
        if (!canHover.matches) return;
        clearTimeout(closeTimer);
        menus.forEach((other) => {
          const otherDetails = other.querySelector('details');
          if (other !== menu && otherDetails?.open) setOpen(otherDetails, false);
        });
        if (!details.open) setOpen(details, true);
      });

      // Pequeña espera al salir para poder llevar el cursor hasta el panel.
      menu.addEventListener('mouseleave', () => {
        if (!canHover.matches) return;
        closeTimer = setTimeout(() => setOpen(details, false), 180);
      });

      // Con el cursor encima, un clic en el botón no debe cerrar el menú.
      summary.addEventListener('click', (event) => {
        if (canHover.matches && details.open && menu.matches(':hover')) event.preventDefault();
      });
    });

    window.addEventListener('resize', () => {
      menus.forEach((menu) => {
        const details = menu.querySelector('details');
        const panel = details?.querySelector('.mega-menu__content');
        if (details?.open && panel) keepPanelInViewport(details, panel);
      });
    });
  }

  // Desplegable propio para "Ordenar por" y las variantes en lista.
  // El <select> nativo sigue en el DOM (oculto) y se actualiza con cada
  // elección, disparando los mismos eventos que escucha el tema.
  const CUSTOM_SELECTS = [
    'facet-filters-form select.facet-filters__sort',
    '.product-form__input--dropdown .select:not(:has(.dropdown-swatch)) > select.select__select',
  ].join(',');
  const SEARCH_MIN_OPTIONS = 8;
  let openSelect = null;

  function closeCustomSelect(instance, focusButton = false) {
    if (!instance) return;
    instance.root.classList.remove('is-open');
    instance.button.setAttribute('aria-expanded', 'false');
    if (focusButton) instance.button.focus();
    if (openSelect === instance) openSelect = null;
  }

  function buildCustomSelect(select) {
    select.dataset.aaEnhanced = 'true';
    const id = `aa-select-${select.id || Math.random().toString(36).slice(2)}`;
    const root = document.createElement('div');
    root.className = 'aa-select';
    if (select.closest('.product-form__input')) root.classList.add('aa-select--start');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'aa-select__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    const labelText = (select.id && document.querySelector(`label[for="${select.id}"]`)?.textContent.trim()) || '';
    button.innerHTML = `<span class="aa-select__value" id="${id}-value"></span><svg class="aa-select__caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>`;

    const panel = document.createElement('div');
    panel.className = 'aa-select__panel';
    let search = null;
    if (select.options.length >= SEARCH_MIN_OPTIONS) {
      search = document.createElement('input');
      search.type = 'search';
      search.className = 'aa-select__search';
      search.placeholder = 'Buscar…';
      search.setAttribute('aria-label', 'Buscar opción');
      search.autocomplete = 'off';
      panel.appendChild(search);
    }
    const list = document.createElement('ul');
    list.className = 'aa-select__list';
    list.id = `${id}-list`;
    list.setAttribute('role', 'listbox');
    button.setAttribute('aria-controls', list.id);
    panel.appendChild(list);

    const empty = document.createElement('li');
    empty.className = 'aa-select__empty';
    empty.textContent = 'Sin resultados';
    empty.hidden = true;

    root.append(button, panel);
    select.insertAdjacentElement('afterend', root);
    select.classList.add('aa-select-native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    const instance = { root, button, select };
    let items = [];
    let active = -1;

    const visibleItems = () => items.filter((item) => !item.hidden);

    const setActive = (index) => {
      const visible = visibleItems();
      if (!visible.length) return;
      active = (index + visible.length) % visible.length;
      items.forEach((item) => item.classList.remove('is-active'));
      const item = visible[active];
      item.classList.add('is-active');
      item.scrollIntoView({ block: 'nearest' });
      (search || button).setAttribute('aria-activedescendant', item.id);
    };

    const render = () => {
      list.textContent = '';
      items = Array.from(select.options).map((option, index) => {
        const item = document.createElement('li');
        item.id = `${id}-opt-${index}`;
        item.className = 'aa-select__option';
        item.setAttribute('role', 'option');
        item.dataset.index = index;
        item.textContent = option.textContent.trim();
        item.setAttribute('aria-selected', String(option.selected));
        if (option.disabled) item.setAttribute('aria-disabled', 'true');
        list.appendChild(item);
        return item;
      });
      list.appendChild(empty);
      const value = select.options[select.selectedIndex]?.textContent.trim() || '';
      button.querySelector('.aa-select__value').textContent = value;
      button.setAttribute('aria-label', labelText ? `${labelText}: ${value}` : value);
    };

    const choose = (index) => {
      const option = select.options[index];
      if (!option || option.disabled) return;
      if (select.selectedIndex !== index) {
        select.selectedIndex = index;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      render();
      closeCustomSelect(instance, true);
    };

    const filter = () => {
      const term = search.value.trim().toLowerCase();
      items.forEach((item) => {
        item.hidden = term !== '' && !item.textContent.toLowerCase().includes(term);
      });
      empty.hidden = visibleItems().length > 0;
      setActive(0);
    };

    const open = () => {
      if (openSelect && openSelect !== instance) closeCustomSelect(openSelect);
      closeFacetPanels();
      render();
      root.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      openSelect = instance;
      const selected = visibleItems().findIndex((item) => item.getAttribute('aria-selected') === 'true');
      setActive(Math.max(selected, 0));
      if (search) {
        search.value = '';
        filter();
        setActive(Math.max(selected, 0));
        search.focus();
      }
    };

    const onKeydown = (event) => {
      const isOpen = root.classList.contains('is-open');
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) return open();
          setActive(active + (event.key === 'ArrowDown' ? 1 : -1));
          break;
        case 'Enter':
        case ' ':
          if (event.key === ' ' && event.target === search) return;
          event.preventDefault();
          if (!isOpen) return open();
          if (visibleItems()[active]) choose(+visibleItems()[active].dataset.index);
          break;
        case 'Escape':
          if (isOpen) {
            event.preventDefault();
            closeCustomSelect(instance, true);
          }
          break;
        case 'Tab':
          closeCustomSelect(instance);
          break;
      }
    };

    button.addEventListener('click', () => (root.classList.contains('is-open') ? closeCustomSelect(instance) : open()));
    button.addEventListener('keydown', onKeydown);
    search?.addEventListener('keydown', onKeydown);
    search?.addEventListener('input', (event) => {
      event.stopPropagation(); // no disparar el filtrado del tema al escribir
      filter();
    });
    list.addEventListener('click', (event) => {
      const item = event.target.closest('.aa-select__option');
      if (item) choose(+item.dataset.index);
    });
    list.addEventListener('mousemove', (event) => {
      const item = event.target.closest('.aa-select__option');
      if (item) setActive(visibleItems().indexOf(item));
    });
    select.addEventListener('change', render);

    render();
  }

  // Filtros de colección: solo un panel abierto a la vez.
  const FACET_PANELS = 'facet-filters-form details.facets__disclosure';

  function closeFacetPanels(except) {
    document.querySelectorAll(`${FACET_PANELS}[open]`).forEach((details) => {
      if (details === except) return;
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  function initExclusiveFacets() {
    // "toggle" no burbujea: se escucha en fase de captura. Sirve también para
    // los filtros que el tema vuelve a dibujar por AJAX.
    document.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!details.matches?.(FACET_PANELS) || !details.open) return;
        closeFacetPanels(details);
        if (openSelect) closeCustomSelect(openSelect);
        details.classList.add('aa-opening');
        setTimeout(() => details.classList.remove('aa-opening'), 400);
      },
      true
    );
  }

  function initCustomSelects(root = document) {
    root.querySelectorAll(CUSTOM_SELECTS).forEach((select) => {
      if (select.dataset.aaEnhanced || select.multiple) return;
      buildCustomSelect(select);
    });
  }

  function initCustomSelectWatchers() {
    initCustomSelects();
    document.addEventListener('click', (event) => {
      if (openSelect && !openSelect.root.contains(event.target)) closeCustomSelect(openSelect);
    });
    // Filtros y variantes se vuelven a dibujar por AJAX: mejorar los nuevos <select>.
    // El observador corre antes de que el navegador pinte, así el <select>
    // nativo nunca llega a verse (antes se esperaba un cuadro y parpadeaba).
    new MutationObserver(() => initCustomSelects()).observe(document.body, { childList: true, subtree: true });
  }

  // Banner principal: mientras el carrusel se desplaza, cada diapositiva recibe
  // su posición relativa (--aa-p: -1 a la izquierda, 0 centrada, 1 a la derecha)
  // para que el CSS haga el paralaje, el zoom y el fundido entre imágenes.
  function initSlideshowTransitions(root = document) {
    if (reduceMotion) return;
    root.querySelectorAll('slideshow-component').forEach((component) => {
      const slider = component.querySelector('.slideshow.slider');
      if (!slider || slider.dataset.aaSlides) return;
      slider.dataset.aaSlides = 'true';
      component.classList.add('aa-slideshow');
      component.style.setProperty('--aa-slide-speed', `${slider.dataset.speed || 5}s`);

      const slides = slider.querySelectorAll('.slideshow__slide');
      let frame = null;
      const paint = () => {
        frame = null;
        const width = slider.clientWidth || 1;
        slides.forEach((slide) => {
          const p = Math.max(-1, Math.min(1, (slide.offsetLeft - slider.scrollLeft) / width));
          slide.style.setProperty('--aa-p', p.toFixed(4));
          slide.style.setProperty('--aa-pa', Math.abs(p).toFixed(4));
        });
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(paint);
      };

      slider.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      paint();

      // El scroll suave nativo es demasiado rápido para lucir el efecto:
      // el cambio de diapositiva se anima a mano con una curva más pausada.
      let animation = null;
      component.setSlidePosition = (position) => {
        if (animation) cancelAnimationFrame(animation);
        component.aaAnimating = false;
        const width = slider.clientWidth || 1;
        const max = slider.scrollWidth - width;
        const target = Math.max(0, Math.min(max, Math.round(position / width) * width));
        const start = slider.scrollLeft;
        const distance = target - start;
        if (!distance) return;
        component.aaAnimating = true;
        const duration = 1100;
        const startTime = performance.now();
        const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        slider.style.scrollSnapType = 'none';
        slider.style.scrollBehavior = 'auto';
        const step = (now) => {
          const t = Math.min(1, (now - startTime) / duration);
          slider.scrollLeft = start + distance * ease(t);
          if (t < 1) {
            animation = requestAnimationFrame(step);
          } else {
            animation = null;
            component.aaAnimating = false;
            slider.style.scrollSnapType = '';
            slider.style.scrollBehavior = '';
          }
        };
        animation = requestAnimationFrame(step);
      };
    });
  }

  // Navegación del banner:
  // - Rotación automática de ida y vuelta: al llegar a la última diapositiva
  //   regresa una a una (de derecha a izquierda) en vez de saltar a la primera.
  // - Flechas y puntos parten siempre de la diapositiva destino (no de la
  //   posición a mitad de camino), así un clic durante un cambio nunca deja
  //   el banner entre dos imágenes.
  // - Cada clic reinicia el temporizador: el cambio automático no interrumpe
  //   al usuario justo después de que eligió una diapositiva.
  function initSlideshowNavigation(root = document) {
    root.querySelectorAll('slideshow-component').forEach((component) => {
      const slider = component.querySelector('.slideshow.slider');
      if (!slider || component.dataset.aaNav) return;
      const total = slider.querySelectorAll('.slideshow__slide').length;
      if (total < 2) return;
      component.dataset.aaNav = 'true';

      const autoplay = slider.dataset.autoplay === 'true';
      const width = () => slider.clientWidth || 1;
      let index = Math.round(slider.scrollLeft / width());
      let direction = 1;

      const goTo = (next) => {
        index = (next + total) % total;
        if (index === total - 1) direction = -1;
        else if (index === 0) direction = 1;
        component.setSlidePosition(index * width());
      };

      // Si el usuario deslizó con el dedo, partir de donde quedó.
      const syncIndex = () => {
        if (!component.aaAnimating) index = Math.round(slider.scrollLeft / width());
      };

      const restartTimer = () => {
        if (!autoplay || typeof component.play !== 'function') return;
        const paused = component.sliderAutoplayButton && !component.autoplayButtonIsSetToPlay;
        if (!paused) component.play();
      };

      // Fase de captura: se atiende aquí antes que los manejadores del tema.
      component.addEventListener(
        'click',
        (event) => {
          const arrow = event.target.closest('button[name="previous"], button[name="next"]');
          const dot = event.target.closest('.slider-counter__link');
          if (!arrow && !dot) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          syncIndex();
          if (arrow) {
            goTo(index + (arrow.name === 'next' ? 1 : -1));
          } else {
            goTo(Array.from(component.querySelectorAll('.slider-counter__link')).indexOf(dot));
          }
          restartTimer();
        },
        true
      );

      if (!autoplay) return;
      component.autoRotateSlides = () => {
        syncIndex();
        goTo(index + direction);
      };

      // Si el carrusel ya arrancó, reiniciarlo para que use la rotación nueva.
      restartTimer();
    });
  }

  function init() {
    document.documentElement.classList.add('aa-js');
    initSlideshowTransitions();
    initSlideshowNavigation();
    initMenuHover();
    initExclusiveFacets();
    initCustomSelectWatchers();
    initReveal();
    initProgressBar();
    initRipple();
    initCartBump();
    initFlyToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initReveal(event.target);
      initSlideshowTransitions(event.target);
      initSlideshowNavigation(event.target);
    });
  }
})();
